import { Icon, Icons } from "@superwall/legacy-ui"
import { DropdownSelect } from "app/core/components/input"
import { SModal, SField, SButton } from "@superwall/legacy-ui"
import _ from "lodash"
import { Fragment, useEffect, useMemo, useState } from "react"
import tw from "tailwind-styled-components"
import { TMarqueeItemCount } from "./Element"
import { useField } from "react-final-form"
import { getBuilderVersion } from "../Actions"
import { useTemplateJsonContext } from "app/paywallBuilderV2/contexts/JsonProvider"

const Base = tw.div`
  border
  border-white
  border-opacity-10
  rounded-md
  overflow-hidden
  w-[644px]
`

const TabsContainer = tw.div`
  flex
  flex-row
  justify-start
  items-start
`

const Tab = tw.div<{ $selected: boolean }>`
  border-t-[2px]
  border-t-[rgba(255,255,255,0)]
  border-r-[1px]
  border-r-[rgba(255,255,255,0.1)]
  border-b-[1px]
  border-b-[rgba(255,255,255,0)]
  mb-[-1px]
  py-2
  px-4
  text-white
  font-semibold
  cursor-pointer

  ${({ $selected }) =>
    $selected
      ? "border-t-accent-light border-b-[#23272e] text-accent-light text-opacity-100"
      : "border-t-[rgba(255,255,255,0)] border-b-[rgba(255,255,255,0)] text-white text-opacity-70"}
`

const ScrollContent = tw.div`
  overflow-scroll
  border-t-[1px]
  border-white
  border-opacity-10
  pt-4
  max-h-[66vh]
`

const ComponentSection = tw.div`
  flex
  flex-col
  flex-wrap
  px-4
`

const Category = tw.div`
  flex
  flex-wrap
  gap-4
  mt-4
`

const ComponentContainer = tw.div<{ $selected: boolean; $isFirstRow: boolean }>`
  flex
  flex-[0_0_calc(25%-12px)]
  flex-col
  items-center
  justify-center
  border
  border-white
  border-opacity-10
  rounded-md
  w-[calc(25%-12px)]
  h-24
  text-white
  text-opacity-70
  cursor-pointer

  hover:!border-accent-light

  ${({ $selected }) => ($selected ? "!border-accent-light !text-accent-light" : "")}
`

const ComponentTitle = tw.div`
  mt-3
  text-center
  text-sm
`

type TComponent = {
  name: string
  value?: string
  icon?: string
}

type TCategory = {
  category: string
  components: TComponent[]
}

type TCategories = TCategory[]

export enum ESelectComponentModalEvent {
  OPEN = "OPEN",
  CLOSE = "CLOSE",
}

export type TOpenSelectComponentModalEvent = CustomEvent<{
  contentType?: "mainContent" | "cardContent" | "backgroundContent" | null
  title?: string
  onAddComponent?: ({
    componentName,
    marqueeItemCount,
    contentType,
  }: {
    componentName: string
    marqueeItemCount: TMarqueeItemCount
    contentType: "mainContent" | "cardContent" | "backgroundContent" | null
  }) => void
  newSiblingPlacement?: "before" | "after" | null
}>

const Elements: TCategories = [
  {
    category: "Basic",
    components: [
      {
        name: "Button",
        value: "buttonBase",
        icon: "MdCrop169",
      },
      {
        name: "Divider",
        icon: "MdHorizontalRule",
      },
      {
        name: "Dividing Header",
        icon: "MdOutlineViewDay",
      },
      {
        name: "Dropdown",
        icon: "MdOutlineTableRows",
      },
      // NOTE: Add back in if we really need this. For now it's not needed. It's also broken.
      // {
      //   name: "HTML",
      //   icon: "MdOutlineCode",
      // },
    ],
  },
  {
    category: "Typography",
    components: [
      {
        name: "Text",
        icon: "MdTextFields",
      },
      {
        name: "Paragraph",
        icon: "MdTextFields",
      },
      {
        name: "Subheading",
        icon: "MdOutlineTitle",
      },
      {
        name: "Heading",
        value: "heading",
        icon: "MdOutlineTitle",
      },
    ],
  },
  {
    category: "Media",
    components: [
      {
        name: "Image",
        value: "imageBase",
        icon: "MdOutlineImage",
      },
      {
        name: "Lottie",
        icon: "MdPhotoFilter",
      },
      {
        name: "Marquee",
        icon: "MdOutlineBurstMode",
      },
      {
        name: "Video",
        icon: "MdOndemandVideo",
      },
    ],
  },
  {
    category: "Layout",
    components: [
      {
        name: "Section",
        icon: "MdOutlineWidthFull",
      },
      {
        name: "Div",
        icon: "MdOutlineSelectAll",
      },
      {
        name: "List",
        value: "listV2",
        icon: "MdFormatListBulleted",
      },
      {
        name: "Container",
        icon: "MdCropSquare",
      },
      {
        name: "Card",
        icon: "MdOutlineSubtitles",
      },
      // {
      //   name: "Modal",
      //   icon: "MdOutlineFilterNone",
      // },
      // {
      //   name: "Close Modal",
      //   value: "closeModal",
      //   icon: "MdOutlineCloseFullscreen",
      // },
      {
        name: "Drawer",
        icon: "MdOpenInBrowser",
      },
      {
        name: "Products",
        icon: "MdStoreMallDirectory",
      },
    ],
  },
]

const Recipes: TCategories = [
  {
    category: "Basic",
    components: [
      {
        name: "Bullet",
        icon: "MdFormatListBulleted",
      },
      {
        name: "Button w/ Label (Horizontal)",
        value: "buttonWithLabelHorizontal",
        icon: "MdSmartButton",
      },
      {
        name: "Button w/ Label (Vertical)",
        value: "buttonWithLabelVertical",
        icon: "MdSmartButton",
      },
      {
        name: "Rating",
        icon: "MdStarHalf",
      },
      {
        name: "Review",
        icon: "MdOutlineRateReview",
      },
      {
        name: "Table",
        icon: "MdOutlineTableChart",
      },
      {
        name: "Timeline",
        icon: "MdOutlineStorage",
      },
    ],
  },
  {
    category: "Typography",
    components: [
      {
        name: "Content Block (default)",
        value: "contentBlock",
        icon: "MdTextFields",
      },
      {
        name: "Content Block (centered)",
        value: "contentBlockCentered",
        icon: "MdTextFields",
      },
      {
        name: "Heading w/ Badge",
        value: "headingWithBadge",
        icon: "MdOutlineTitle",
      },
    ],
  },
  {
    category: "Media",
    components: [
      {
        name: "Image w/ Settings",
        value: "image",
        icon: "MdOutlineImage",
      },
    ],
  },
  {
    category: "Layout",
    components: [
      {
        name: "Footer (default)",
        value: "footer",
        icon: "MdOutlineWidthFull",
      },
      {
        name: "Footer (compact)",
        value: "footerCompact",
        icon: "MdOutlineWidthFull",
      },
      {
        name: "Footer (minimal)",
        value: "footerMinimal",
        icon: "MdOutlineWidthFull",
      },
    ],
  },
]

const marqueeItemOptions = [
  { label: "6", value: "6" },
  { label: "7", value: "7" },
  { label: "8", value: "8" },
  { label: "9", value: "9" },
  { label: "10", value: "10" },
]

type TOnAddComponent = {
  componentName: string
  marqueeItemCount: TMarqueeItemCount
  contentType: "mainContent" | "cardContent" | "backgroundContent" | null
  newSiblingPlacement: "before" | "after" | null
}

const SelectComponentModal = () => {
  const { parsedJson } = useTemplateJsonContext()
  const { pageSettings } = parsedJson
  const [isOpen, setIsOpen] = useState(false)
  const [contentType, setContentType] = useState<
    "mainContent" | "cardContent" | "backgroundContent" | null
  >()
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null)
  const [onAddComponent, setOnAddComponent] = useState<
    ({ componentName, marqueeItemCount, contentType, newSiblingPlacement }: TOnAddComponent) => void
  >(() => () => {})
  const [marqueeItemCount, setMarqueeItemCount] = useState<TMarqueeItemCount>("6")
  const [title, setTitle] = useState("")
  const [newSiblingPlacement, setNewSiblingPlacement] = useState<
    "before" | "after" | null | undefined
  >(null)

  const handleOpenModal = (e: TOpenSelectComponentModalEvent) => {
    // Reset modal state
    setMarqueeItemCount("6")
    setSelectedComponent(null)

    // Set new modal state and callbacks
    const { detail } = e || {}
    const { contentType, title, onAddComponent, newSiblingPlacement } = detail || {}
    setIsOpen(true)
    setContentType(contentType)
    setTitle(title || "")
    setOnAddComponent((props: TOnAddComponent) => onAddComponent)
    setNewSiblingPlacement(newSiblingPlacement)
  }

  useEffect(() => {
    document.addEventListener(ESelectComponentModalEvent.OPEN, handleOpenModal)

    return () => {
      document.removeEventListener(ESelectComponentModalEvent.OPEN, handleOpenModal)
    }
  })

  const handleCloseModal = () => {
    setIsOpen(false)
  }

  useEffect(() => {
    document.addEventListener(ESelectComponentModalEvent.CLOSE, handleCloseModal)

    return () => {
      document.removeEventListener(ESelectComponentModalEvent.CLOSE, handleCloseModal)
    }
  })

  const filteredElements = useMemo(() => {
    return contentType === "backgroundContent"
      ? Elements.map((element) => {
          if (element.category !== "Layout") return element

          return {
            ...element,
            components: element.components.filter(
              (component) => component.name !== "Drawer" && component.name !== "Products"
            ),
          }
        })
      : pageSettings?.hideFixedPurchaseDrawer === "true"
        ? Elements
        : Elements.map((element) => {
            if (element.category !== "Layout") return element

            return {
              ...element,
              components: element.components.filter(
                (component) => component.name !== "Drawer" && component.name !== "Products"
              ),
            }
          })
  }, [pageSettings?.hideFixedPurchaseDrawer, contentType])

  const filteredRecipes = useMemo(() => {
    return pageSettings?.hideFixedPurchaseDrawer === "true"
      ? Recipes
      : Recipes.filter((recipe) => recipe.category !== "Layout")
  }, [pageSettings?.hideFixedPurchaseDrawer])

  const [selectedTab, setSelectedTab] = useState<"elements" | "recipes">("elements")
  const {
    input: { value: url },
  } = useField("url")
  const builderVersion = getBuilderVersion(url)
  const modalVersionMinimum = "1.49.22"

  const isAddDisabled = !selectedComponent

  const onClickTab = (tab: "elements" | "recipes") => {
    if (tab === selectedTab) return
    setSelectedTab(tab)
    setSelectedComponent(null)
  }

  const onClickComponent = (component: TComponent) => {
    setSelectedComponent(component?.value || _.camelCase(component?.name))
  }

  const _onClose = () => {
    setIsOpen(false)
    setTimeout(() => {
      setSelectedTab("elements")
    }, 500)
  }

  const onClickAddComponent = () => {
    if (typeof onAddComponent === "function") {
      onAddComponent({
        componentName: selectedComponent!,
        marqueeItemCount: marqueeItemCount,
        contentType: contentType || null,
        newSiblingPlacement: newSiblingPlacement || null,
      })
    }
  }

  return (
    <SModal isOpen={isOpen} onClose={_onClose}>
      <SModal.Content onClose={_onClose} maxWidth="3xl">
        <SModal.Title>{title}</SModal.Title>
        <Base>
          <TabsContainer>
            <Tab $selected={selectedTab === "elements"} onClick={() => onClickTab("elements")}>
              Elements
            </Tab>
            <Tab $selected={selectedTab === "recipes"} onClick={() => onClickTab("recipes")}>
              Recipes
            </Tab>
          </TabsContainer>
          <ScrollContent>
            <ComponentSection>
              {selectedTab === "elements" ? (
                <>
                  {_.map(filteredElements, (element) => {
                    return (
                      <Fragment key={element.category}>
                        <div className="mb-8">
                          <div className="ml-1 text-white text-opacity-80">{element.category}</div>
                          <Category>
                            {_.map(element.components, (component, index) => {
                              if (
                                (component.name === "Modal" || component.name === "Close Modal") &&
                                builderVersion < modalVersionMinimum
                              ) {
                                return null
                              }
                              const name = (component?.icon || "MdOutlineAdd") as keyof typeof Icons

                              const isSelected =
                                selectedComponent === component?.value ||
                                selectedComponent === _.camelCase(component?.name)
                              return (
                                <ComponentContainer
                                  key={component?.name}
                                  onClick={() => onClickComponent(component)}
                                  $selected={isSelected}
                                  $isFirstRow={index < 4}
                                >
                                  <Icon
                                    name={name}
                                    size="xl"
                                    color={isSelected ? "accentLight" : "whiteOpacity20"}
                                  />
                                  <ComponentTitle>{component?.name}</ComponentTitle>
                                </ComponentContainer>
                              )
                            })}
                          </Category>
                        </div>
                        {/* Marquee item count required if Marquee is selected. */}
                        {selectedComponent === "marquee" && element.category === "Media" && (
                          <SField>
                            <SField.Label>Number Of Marquee Items</SField.Label>
                            <DropdownSelect
                              overrideWrapperClassname={`w-full text-sm font-normal`}
                              options={marqueeItemOptions}
                              value={
                                marqueeItemOptions.find(
                                  (option) => option.value == marqueeItemCount
                                ) ?? {
                                  value: "6",
                                  label: "6",
                                }
                              }
                              onChange={(option) =>
                                setMarqueeItemCount(option?.value as TMarqueeItemCount)
                              }
                              selectClassName="bg-grayscale-medium"
                            />
                            <br />
                          </SField>
                        )}
                      </Fragment>
                    )
                  })}
                </>
              ) : (
                <>
                  {_.map(filteredRecipes, (recipe) => {
                    return (
                      <Fragment key={recipe.category}>
                        <div className="mb-8">
                          <div className="ml-1 text-white text-opacity-80">{recipe.category}</div>
                          <Category>
                            {_.map(recipe.components, (component, index) => {
                              const name = (component?.icon || "MdOutlineAdd") as keyof typeof Icons
                              return (
                                <ComponentContainer
                                  key={component?.name}
                                  onClick={() => onClickComponent(component)}
                                  $selected={
                                    selectedComponent === component?.value ||
                                    selectedComponent === _.camelCase(component?.name)
                                  }
                                  $isFirstRow={index < 4}
                                >
                                  <Icon name={name} size="xl" color="whiteOpacity20" />
                                  <ComponentTitle>{component?.name}</ComponentTitle>
                                </ComponentContainer>
                              )
                            })}
                          </Category>
                        </div>
                      </Fragment>
                    )
                  })}
                </>
              )}
            </ComponentSection>
          </ScrollContent>
        </Base>
        <SButton onClick={onClickAddComponent} className="mt-2 w-full" disabled={isAddDisabled}>
          Add
        </SButton>
      </SModal.Content>
    </SModal>
  )
}

export default SelectComponentModal
